import { supabase } from "@/integrations/supabase/client";

export interface WallModel {
  id: string;
  name: string;
  profile_image_url: string | null;
  agency_name: string | null;
  public_handle: string | null;
  public_handle_enabled: boolean | null;
}

export const FEATURED_WALL_SETTING_KEY = "landing_featured_models";

/** Accepts a full share URL (focuscasting.com/p/<token>) or a bare token. */
export function parsePackageShareToken(input: string): string | null {
  const value = input.trim();
  if (!value) return null;
  const fromUrl = value.match(/\/p\/([A-Za-z0-9_-]+)/);
  if (fromUrl) return fromUrl[1];
  return /^[A-Za-z0-9_-]+$/.test(value) ? value : null;
}

interface FeaturedFetch {
  models: WallModel[];
  curated: boolean;
}

function mapRows(rows: any[] | null): WallModel[] {
  return (rows ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    profile_image_url: row.profile_image_url,
    agency_name: row.agencies?.name ?? null,
    public_handle: row.public_handle,
    public_handle_enabled: row.public_handle_enabled,
  }));
}

/**
 * Fetches the talent wall for landing surfaces. When a curated featured list
 * exists it is used exactly as picked (no automatic top-up); otherwise the
 * wall falls back to the default public catalog order.
 */
export async function fetchWallModels(limit: number): Promise<FeaturedFetch> {
  const fallback = async (): Promise<FeaturedFetch> => {
    const { data } = await supabase
      .from("models_public")
      .select("id, name, profile_image_url, public_handle, public_handle_enabled, agencies(name)")
      .not("profile_image_url", "is", null)
      .limit(limit);
    return { models: mapRows(data as any[] | null), curated: false };
  };

  try {
    const { data: setting } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", FEATURED_WALL_SETTING_KEY)
      .maybeSingle();

    const rawIds = (setting?.value as any)?.model_ids;
    const ids: string[] = Array.isArray(rawIds) ? rawIds.filter((v: unknown): v is string => typeof v === "string") : [];
    if (!ids.length) return await fallback();

    const { data } = await supabase
      .from("models_public")
      .select("id, name, profile_image_url, public_handle, public_handle_enabled, agencies(name)")
      .in("id", ids);
    const byId = new Map(mapRows(data as any[] | null).map((m) => [m.id, m]));
    const curated = ids
      .map((id) => byId.get(id))
      .filter((m): m is WallModel => Boolean(m?.profile_image_url))
      .slice(0, limit);
    if (!curated.length) return await fallback();
    return { models: curated, curated: true };
  } catch {
    return await fallback();
  }
}
